import { BriefingSlot } from "@/features/briefing";
import { selectMessageById, useChatStore } from "@/features/chat";
import { MemoryConsentBanner } from "@/features/settings";
import { FocusedViewLayout, clearFocusKeepMsg, inboxItemFromSource } from "@/features/sources";
import { InboxDetailPane } from "@/features/sources/components/InboxDetailPane";
import { useEmailSummary } from "@/features/sources/hooks/useEmailSummary";
import { emailThreadQueryOptions } from "@/features/sources/hooks/useEmailThread";
import { useQueryClient } from "@tanstack/react-query";
import { validateConversationsSearch } from "@/features/workspace-router";
import { InboxItemRow } from "@/features/workspace/components/InboxItemRow";
import { QuickAddMenu } from "@/features/workspace/components/QuickAddMenu";
import { SubTabBar, useActiveSubTab } from "@/features/workspace/components/SubTabBar";
import type { PeriodPreset } from "@/features/workspace/components/searchFilters/types";
import type { SubTabDef } from "@/features/workspace/config";
import { formatRelativeTime } from "@/features/workspace/hooks/formatRelativeTime";
import { useActiveConnections } from "@/features/workspace/hooks/useActiveConnections";
import {
  type ConversationConnection,
  useConversationConnections,
} from "@/features/workspace/hooks/useConversationConnections";
import {
  inboxLiveInfiniteQueryOptions,
  useInboxLiveQuery,
} from "@/features/workspace/hooks/useInboxLiveQuery";
import { useListKeyboardNav } from "@/features/workspace/hooks/useListKeyboardNav";
import { useSearchNavigation } from "@/features/workspace/hooks/useSearchNavigation";
import {
  InboxBulkActionBar,
  type InboxGroupingMode,
  InboxGroupingToggle,
  type InboxSmartChipDef,
  type InboxSmartView,
  encodeInboxGroupingMode,
  encodeInboxSmartView,
  filterByInboxSmartView,
  groupByThread,
  matchesInboxSmartView,
  parseInboxGroupingMode,
  parseInboxSmartView,
  useInboxMutations,
  useIsStarred,
  useSnoozedIds,
} from "@/features/workspace/inbox";
import { ConversationsFiltersPopover } from "@/features/workspace/inbox/components/ConversationsFiltersPopover";
import {
  type InboxDropdownFilters,
  matchesInboxDropdowns,
} from "@/features/workspace/inbox/utils/inboxFilters";
import type {
  InboxChannel,
  InboxItem,
  InboxLiveItem,
  InboxLiveProvider,
  InboxProviderId,
} from "@ando/core";
import {
  Button,
  ConnectionPicker,
  type ConnectionPickerOption,
  SurfaceCommandBar,
  SurfaceEmptyState,
  SurfaceLayout,
  SurfaceList,
  WorkspaceSearchBar,
} from "@ando/ui";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { ArrowLeft, Inbox, Loader2, Mail, MessageSquare, Shield } from "lucide-react";
import type * as React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export const Route = createFileRoute("/_authed/workspace/conversations")({
  validateSearch: validateConversationsSearch,
  // Search-dependent preload — re-runs when view/direction/q change so the
  // warmed cache slice always tracks the URL.
  loaderDeps: ({ search }) => ({
    view: search.view,
    direction: search.direction,
    q: search.q,
  }),
  // Warm the live inbox (highest-latency feed: provider-bound) on hover/focus.
  // Mirrors ConversationsHub's provider-pushdown derivation so the loader hits
  // the EXACT cache slice the component reads.
  loader: ({ context: { queryClient }, deps }) => {
    const smartView = parseInboxSmartView(deps.view);
    const view = PROVIDER_PUSHDOWN_VIEWS.has(smartView) ? smartView : undefined;
    void queryClient.ensureInfiniteQueryData(
      inboxLiveInfiniteQueryOptions({ view, direction: deps.direction, q: deps.q }),
    );
  },
  component: ConversationsHub,
});

/**
 * Smart-views with a provider-native query equivalent (Jun 24, 2026).
 * For these the live feed pushes the filter down to Gmail (``q=``) /
 * Graph (``$filter=``) so the BE fetches only the matching slice
 * instead of fetching + the FE discarding.
 *
 * NOT in this set:
 *   - ``tutti``: no filter, default behaviour
 *   - ``today``: TZ-local "started after midnight" — relies on the
 *     user's device clock, not the server's, so it stays client-side
 *   - ``needs_reply``: heuristic IT/EN regex over snippet — no
 *     provider equivalent
 */
const PROVIDER_PUSHDOWN_VIEWS: ReadonlySet<InboxSmartView> = new Set([
  "unread",
  "starred",
  "attachments",
]);

/**
 * Providers whose FREE-TEXT search is pushed to the provider (Gmail ``q=`` /
 * Outlook ``$search``) — their live pages are already filtered server-side, so
 * the FE must NOT re-filter them by substring (that would drop body-only matches
 * and re-trigger the infinite-scroll history hunt). Everything else (PEC, chat)
 * keeps the client-side gate.
 */
const SEARCH_PUSHDOWN_PROVIDERS: ReadonlySet<InboxLiveProvider> = new Set<InboxLiveProvider>([
  "gmail",
  "outlook",
]);

/**
 * Conversazioni hub — unified inbox feed.
 *
 * June 2026 — "agentic SaaS" pass:
 *
 *  - **Smart view chips** (Tutti / Da leggere / Richiede risposta /
 *    Con allegato / Stellate / Oggi) above the per-channel sub-tab
 *    bar; URL-persisted via ``?view=``.
 *  - **Thread grouping** by ``InboxLiveItem.thread_id`` with a
 *    ``?group=thread|flat`` toggle in the header.
 *  - **Per-row hover actions**: mark read/unread, snooze, star,
 *    archive, reply. Bulk action bar mirrors the documents pattern.
 *  - **Gmail-style keyboard map** via :func:`useListKeyboardNav`:
 *    j/k navigation, Enter open, Space toggle select, Cmd+A select
 *    all, ``e`` archive, ``u`` mark read, ``s`` snooze, ``#`` archive,
 *    ``r`` reply, ``x`` toggle select.
 *  - **Inline AI summary** for unread rows (lazy-loaded via
 *    ``useEmailSummary`` behind IntersectionObserver, capped to 8
 *    concurrent fetches via :class:`AiSummaryGate`).
 *  - **Snooze** (FE-only until BE column lands) — rows disappear from
 *    the list until the chosen epoch.
 *  - **Compose** is started from the unified :class:`NewActionMenu`
 *    ("Componi"), which opens the email/PEC/message action in the
 *    working area (account selection lives in that form) — no drawer.
 *
 * The earlier ``ConnectionPicker`` + per-channel sub-tab partition
 * + ``?focus=`` inline viewer behaviour is preserved verbatim.
 */

/**
 * Live-feed providers visible behind a sub-tab. Email still groups
 * gmail + outlook, PEC stays pec_aruba, chat chips re-route 1:1.
 */
function providersForSubTab(subTabId: string): ReadonlySet<InboxLiveProvider> | undefined {
  switch (subTabId) {
    case "email":
      return new Set<InboxLiveProvider>(["gmail", "outlook"]);
    case "pec":
      return new Set<InboxLiveProvider>(["pec_aruba"]);
    case "slack":
      return new Set<InboxLiveProvider>(["slack"]);
    case "teams":
      return new Set<InboxLiveProvider>(["teams"]);
    case "google_chat":
      return new Set<InboxLiveProvider>(["google_chat"]);
    default:
      return undefined;
  }
}

/** Live provider → row channel for ``InboxItemRow`` chrome. */
function channelForLiveProvider(provider: InboxLiveProvider): InboxChannel {
  switch (provider) {
    case "pec_aruba":
      return "pec";
    case "slack":
      return "slack";
    case "teams":
      return "teams";
    case "google_chat":
      return "google_chat";
    default:
      return "email";
  }
}

/** Live provider → ``InboxProviderId``. */
function providerIdForLive(provider: InboxLiveProvider): InboxProviderId {
  switch (provider) {
    case "gmail":
      return "gmail";
    case "outlook":
      return "outlook";
    case "slack":
      return "slack";
    case "teams":
      return "teams";
    case "google_chat":
      return "google_chat";
    default:
      return "aruba_pec";
  }
}

/** Adapt ``InboxLiveItem`` → legacy ``InboxItem`` for downstream rendering. */
function liveToInboxItem(live: InboxLiveItem): InboxItem {
  const isChat = live.kind === "chat";
  return {
    message_id: live.message_id,
    channel: channelForLiveProvider(live.provider),
    provider_id: providerIdForLive(live.provider),
    sender_display: live.sender_display,
    subject: live.subject,
    preview: live.snippet,
    received_at: live.received_at,
    has_attachments: live.has_attachments,
    is_unread: isChat ? true : !live.is_read,
    web_link: live.web_link ? live.web_link : undefined,
    live_provider: live.provider,
    live_account: live.account || undefined,
    live_conversation_id: isChat ? live.conversation_id || live.thread_id : undefined,
  };
}

// ── Connection mapping ────────────────────────────────────────────────

function connectionToOption(c: ConversationConnection): ConnectionPickerOption {
  return {
    id: c.id,
    provider: c.vendor,
    account_label: c.connection_key,
  };
}

function ConversationsHub() {
  const { i18n } = useLingui();
  const navigate = useNavigate();
  const search = useSearch({ from: "/_authed/workspace/conversations" });
  const searchNav = useSearchNavigation<{
    providers: ReadonlyArray<string>;
    is_unread: boolean;
    has_attachments: boolean;
    is_important: boolean;
    period: string;
    direction: string | undefined;
  }>("/workspace/conversations", { keyMap: { is_unread: "unread" } });
  const focusId = search.focus ?? null;
  const messageId = (search as { msg?: string }).msg ?? null;
  const activeSubTab = useActiveSubTab("conversations");
  const subTabProviders = useMemo(() => providersForSubTab(activeSubTab), [activeSubTab]);
  const smartView: InboxSmartView = parseInboxSmartView(search.view);

  // Jun 24, 2026 — only the smart-views with a provider-native query
  // equivalent get pushed down to the BE (Gmail ``q=`` / Graph
  // ``$filter=``). Other views stay post-filters on the client below.
  const liveQueryView = PROVIDER_PUSHDOWN_VIEWS.has(smartView) ? smartView : undefined;
  const groupingMode: InboxGroupingMode = parseInboxGroupingMode(search.group);

  // Live feed — ``view`` pushed down to the provider (Gmail q= / Graph
  // $filter=) when the smart-view has a native equivalent. Other views
  // stay post-filters on the client below.
  const {
    items: liveItems,
    isLoading,
    isError,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInboxLiveQuery({
    view: liveQueryView,
    direction: search.direction,
    // Push the free-text search to the provider (Gmail q= / Outlook $search)
    // so it covers the whole mailbox — otherwise the infinite scroll hunts the
    // entire history over a client-side substring filter (the "Findomestic loop").
    q: search.q,
  });
  const selectConversation = useChatStore((s) => s.selectConversation);
  const setComposerDraft = useChatStore((s) => s.setComposerDraft);

  // ── Mutations + local stores ──────────────────────────────────────────
  const { markRead, markReadMany, archive, archiveMany, snooze, snoozeMany, toggleStar } =
    useInboxMutations();
  const snoozedIds = useSnoozedIds();

  // ── Deep-link composer seed (unchanged) ───────────────────────────────
  const promptParam = search.prompt;
  useEffect(() => {
    if (!promptParam) return;
    selectConversation(null);
    setComposerDraft(promptParam);
    void navigate({
      to: "/workspace/conversations",
      search: (prev) => ({ ...prev, prompt: undefined }),
      replace: true,
    });
  }, [promptParam, selectConversation, setComposerDraft, navigate]);

  // ── Connection picker plumbing (unchanged) ────────────────────────────
  const { connections: rawConnections } = useConversationConnections();
  const connectionOptions = useMemo<readonly ConnectionPickerOption[]>(
    () => rawConnections.map(connectionToOption),
    [rawConnections],
  );
  const {
    activeIds,
    setActiveIds,
    isReady: connsReady,
  } = useActiveConnections("conversations", connectionOptions);
  const activeAccountKeys = useMemo<ReadonlySet<string>>(() => {
    const activeSet = new Set(activeIds);
    const out = new Set<string>();
    for (const c of rawConnections) {
      if (activeSet.has(c.id) && c.connection_key) out.add(c.connection_key);
    }
    return out;
  }, [rawConnections, activeIds]);
  const knownAccountKeys = useMemo<ReadonlySet<string>>(() => {
    const out = new Set<string>();
    for (const c of rawConnections) {
      if (c.connection_key) out.add(c.connection_key);
    }
    return out;
  }, [rawConnections]);

  // ── Inline-viewer fallback (unchanged) ────────────────────────────────
  const allItems = useMemo<readonly InboxItem[]>(() => liveItems.map(liveToInboxItem), [liveItems]);
  const focusedMessage = useChatStore(selectMessageById(messageId));
  const focusedItem = useMemo<InboxItem | null>(() => {
    if (!focusId) return null;
    return allItems.find((it) => it.message_id === focusId) ?? null;
  }, [focusId, allItems]);
  const focusedItemFromSource = useMemo<InboxItem | null>(() => {
    if (!focusId || focusedItem) return null;
    const src = focusedMessage?.sources?.find((s) => s.doc_id === focusId);
    if (!src) return null;
    return inboxItemFromSource(src);
  }, [focusId, focusedItem, focusedMessage]);
  const resolvedItem = focusedItem ?? focusedItemFromSource;

  // ── Filter pipeline ───────────────────────────────────────────────────
  //
  // Pre-gate (account / sub-tab / snooze + top-row dropdowns) → smart view →
  // free-text query. The pre-gate produces the canonical "base" set used
  // both by ``visibleLiveItems`` AND the smart-chip badge counters
  // (``smartChipDefs``), so the chip "Da leggere 23" can never lead to an
  // empty list because of an invisible dropdown filter.
  //
  // Top-row dropdowns (Provider/Stato/Periodo/Altro) used to be ignored
  // by this pipeline (audit C1 Jun 11, 2026). They now go through
  // ``matchesInboxDropdowns``, applied AS PART OF THE BASE GATES.
  // Jul 2026 dedup — only ``providers`` + ``period`` remain here; unread /
  // attachment / starred moved to the smart-view chips (one filter, one place).
  const dropdownFilters = useMemo<InboxDropdownFilters>(
    () => ({
      providers: search.providers ?? [],
      period: (search.period ?? "all") as PeriodPreset,
    }),
    [search.providers, search.period],
  );

  const applyBaseGates = useCallback(
    (live: InboxLiveItem, now: Date): boolean => {
      if (subTabProviders && !subTabProviders.has(live.provider)) return false;
      const accountGateActive = connsReady && connectionOptions.length > 0 && activeIds.length > 0;
      if (accountGateActive) {
        const mailbox = live.account;
        if (mailbox && knownAccountKeys.has(mailbox) && !activeAccountKeys.has(mailbox)) {
          return false;
        }
      }
      if (snoozedIds.has(live.message_id)) return false;
      if (!matchesInboxDropdowns(live, dropdownFilters, now)) return false;
      return true;
    },
    [
      subTabProviders,
      connsReady,
      connectionOptions.length,
      activeIds.length,
      activeAccountKeys,
      knownAccountKeys,
      snoozedIds,
      dropdownFilters,
    ],
  );

  const visibleLiveItems = useMemo<readonly InboxLiveItem[]>(() => {
    const q = (search.q ?? "").trim().toLowerCase();
    const now = new Date();
    return liveItems.filter((live) => {
      if (!applyBaseGates(live, now)) return false;
      if (!matchesInboxSmartView(live, smartView, now)) return false;
      // Search push-down: Gmail (``q=``) + Outlook (``$search``) run the search
      // AT THE PROVIDER, so their pages come back match-heavy over the WHOLE
      // mailbox — re-filtering them client-side would drop body-only matches →
      // short list → the infinite scroll hunts the history again. Providers we
      // DON'T push search to (PEC, chat) keep the client-side substring gate so
      // they still filter correctly (bounded — those inboxes are small).
      if (q.length > 0 && !SEARCH_PUSHDOWN_PROVIDERS.has(live.provider)) {
        const hay =
          `${live.subject ?? ""} ${live.sender_display ?? ""} ${live.snippet ?? ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [liveItems, applyBaseGates, smartView, search.q]);

  // ── Grouping (thread vs flat) ─────────────────────────────────────────
  //
  // ``flat`` synthesises a 1-message group per row so the rendering path
  // is the same as thread mode (one component, one selection model).
  const threadGroups = useMemo(() => {
    if (groupingMode === "flat") {
      return visibleLiveItems.map((live) => ({
        thread_id: live.message_id,
        newest: live,
        count: 1,
        unread_count: live.is_read ? 0 : 1,
        has_unread: !live.is_read,
        has_attachments_any: live.has_attachments,
        is_starred_any: live.is_starred,
        message_ids: [live.message_id] as ReadonlyArray<string>,
      }));
    }
    return groupByThread(visibleLiveItems);
  }, [visibleLiveItems, groupingMode]);

  // Live-item lookup by message_id — used for hover actions that
  // resolve the underlying ``InboxLiveItem`` from the row's id.
  const liveById = useMemo(() => {
    const m = new Map<string, InboxLiveItem>();
    for (const it of liveItems) m.set(it.message_id, it);
    return m;
  }, [liveItems]);

  // ── Selection state ───────────────────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(() => new Set());
  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);
  const deselectAll = useCallback(() => {
    setSelectedIds((prev) => (prev.size === 0 ? prev : new Set()));
  }, []);
  // Visible ids in render order — used for select-all + keyboard nav.
  const visibleIds = useMemo<readonly string[]>(
    () => threadGroups.map((g) => g.newest.message_id),
    [threadGroups],
  );
  const selectAllVisible = useCallback(() => {
    setSelectedIds(new Set(visibleIds));
  }, [visibleIds]);

  // Resolve "selected → live items" once for the bulk action handlers.
  const selectedItemsLive = useMemo<ReadonlyArray<InboxLiveItem>>(() => {
    const out: InboxLiveItem[] = [];
    for (const id of selectedIds) {
      const live = liveById.get(id);
      if (live) out.push(live);
    }
    return out;
  }, [selectedIds, liveById]);

  // ── AI-summary lazy gate ──────────────────────────────────────────────
  //
  // Single-flight design: the FIRST row that enters view "wins" the slot
  // and its summary is fetched via ``useEmailSummary``. Other rows in view
  // see ``null`` until they hover/click (which routes through a different
  // pane). This is intentional — production rendered ~12+ rows visible at
  // once; if every row had its own ``setActiveSummaryId`` trigger, the
  // callback identity churn made each setState re-render all rows, which
  // re-fired their inView IO callback → ping-pong setState across rows →
  // React #185 ``Maximum update depth exceeded`` → tab freeze with
  // ``RESULT_CODE_HUNG`` in prod (Jun 5, 2026 incident). Dev mode
  // tolerated the loop because non-minified React absorbs/batches more
  // aggressively; prod minified did not.
  //
  // Why a ref + no-deps callback: the callback identity is STABLE across
  // renders (deps = only the landed summary value), so the row's prop
  // doesn't change every tick → no spurious row re-renders → no ping-pong.
  const activeIdRef = useRef<string | null>(null);
  const [activeSummaryId, setActiveSummaryId] = useState<string | null>(null);
  const activeSummary = useEmailSummary(activeSummaryId ?? undefined, {
    enabled: Boolean(activeSummaryId),
  });
  const landedSummary = activeSummary.data?.summary;

  const resolvedSummaryProvider = useCallback(
    (mid: string): string | null => {
      // Claim the slot on the first call (ref check is sync; the state
      // setter is microtask-scheduled to avoid render-in-render).
      if (activeIdRef.current === null) {
        activeIdRef.current = mid;
        queueMicrotask(() => setActiveSummaryId(mid));
      }
      return mid === activeIdRef.current ? (landedSummary ?? null) : null;
    },
    [landedSummary],
  );

  // ── Per-view badge counts ─────────────────────────────────────────────
  // Jun 11, 2026 (audit H1): badges share the SAME base pipeline as the
  // visible list (account + sub-tab + snoozed + top-row dropdowns). Each
  // chip then layers its OWN smart-view predicate. Without this DRY share,
  // clicking "Da leggere 23" could land on an empty list because an
  // invisible dropdown filter (period / has_attachment / …) hid the rows.
  const smartChipDefs: ReadonlyArray<InboxSmartChipDef> = useMemo(() => {
    const now = new Date();
    const base = liveItems.filter((live) => applyBaseGates(live, now));
    return [
      { id: "tutti", badge: base.length },
      { id: "unread", badge: filterByInboxSmartView(base, "unread", now).length },
      { id: "needs_reply", badge: filterByInboxSmartView(base, "needs_reply", now).length },
      { id: "attachments", badge: filterByInboxSmartView(base, "attachments", now).length },
      { id: "starred", badge: filterByInboxSmartView(base, "starred", now).length },
      { id: "today", badge: filterByInboxSmartView(base, "today", now).length },
    ];
  }, [liveItems, applyBaseGates]);

  // ── Channel sub-tab badges ────────────────────────────────────────────
  const unreadByProvider = useMemo(() => {
    let email = 0;
    let pec = 0;
    let slack = 0;
    let teams = 0;
    let googleChat = 0;
    for (const live of liveItems) {
      if (live.kind === "chat") {
        if (live.provider === "slack") slack += 1;
        else if (live.provider === "teams") teams += 1;
        else if (live.provider === "google_chat") googleChat += 1;
        continue;
      }
      if (live.is_read) continue;
      if (live.provider === "pec_aruba") pec += 1;
      else email += 1;
    }
    return {
      email,
      pec,
      slack,
      teams,
      google_chat: googleChat,
      total: email + pec + slack + teams + googleChat,
    };
  }, [liveItems]);
  const totalUnread = unreadByProvider.total;

  const hasProviderConnection = useCallback(
    (provider: InboxLiveProvider): boolean => {
      for (const c of rawConnections) {
        if (c.vendor === provider) return true;
      }
      return false;
    },
    [rawConnections],
  );

  const chips = useMemo<readonly SubTabDef[]>(() => {
    // Badges count only what's loaded so far — mirror the header's "+" so a
    // growing figure reads as "at least N" while more pages remain.
    const withMore = (n: number): number | string => (n > 0 && hasNextPage ? `${n}+` : n);
    const out: SubTabDef[] = [
      { id: "all", label: msg`Tutti`, badge: withMore(totalUnread) },
      { id: "email", label: msg`Email`, badge: withMore(unreadByProvider.email) },
      { id: "pec", label: msg`PEC`, badge: withMore(unreadByProvider.pec) },
    ];
    if (hasProviderConnection("slack") || unreadByProvider.slack > 0) {
      out.push({ id: "slack", label: msg`Slack`, badge: withMore(unreadByProvider.slack) });
    }
    if (hasProviderConnection("teams") || unreadByProvider.teams > 0) {
      out.push({ id: "teams", label: msg`Teams`, badge: withMore(unreadByProvider.teams) });
    }
    if (hasProviderConnection("google_chat") || unreadByProvider.google_chat > 0) {
      out.push({
        id: "google_chat",
        label: msg`Google Chat`,
        badge: withMore(unreadByProvider.google_chat),
      });
    }
    return out;
  }, [unreadByProvider, totalUnread, hasProviderConnection, hasNextPage]);

  // ── URL navigators ────────────────────────────────────────────────────
  const setSmartView = useCallback(
    (next: InboxSmartView) => {
      navigate({
        to: "/workspace/conversations",
        search: (prev: Record<string, unknown>) => ({
          ...prev,
          view: encodeInboxSmartView(next),
        }),
      } as Parameters<typeof navigate>[0]);
    },
    [navigate],
  );

  const setGroupingMode = useCallback(
    (next: InboxGroupingMode) => {
      navigate({
        to: "/workspace/conversations",
        search: (prev: Record<string, unknown>) => ({
          ...prev,
          group: encodeInboxGroupingMode(next),
        }),
      } as Parameters<typeof navigate>[0]);
    },
    [navigate],
  );

  // ── Row open ─────────────────────────────────────────────────────────
  const openRow = useCallback(
    (mid: string) => {
      // Mark-as-read fire-and-forget — the live feed will reconcile via
      // the optimistic write in useInboxMutations.
      const live = liveById.get(mid);
      if (live && !live.is_read) {
        void markRead(mid, true);
      }
      void navigate({
        to: "/workspace/conversations",
        search: { focus: mid },
      });
    },
    [liveById, markRead, navigate],
  );

  // ── Keyboard nav ─────────────────────────────────────────────────────
  const searchAriaLabel = i18n._(msg`Cerca conversazioni`);
  const focusSearch = useCallback(() => {
    const input = document.querySelector<HTMLInputElement>(
      `input[aria-label="${searchAriaLabel}"]`,
    );
    if (input) {
      input.focus();
      input.select?.();
    }
  }, [searchAriaLabel]);

  const keyboardActions = useMemo(
    () => [
      {
        key: "e",
        run: (id: string) => {
          const live = liveById.get(id);
          if (live) void archive(live);
        },
      },
      {
        key: "#",
        run: (id: string) => {
          const live = liveById.get(id);
          if (live) void archive(live);
        },
      },
      {
        key: "u",
        run: (id: string) => {
          const live = liveById.get(id);
          if (!live) return;
          void markRead(id, !live.is_read);
        },
      },
      {
        key: "s",
        run: (id: string) => {
          // Default snooze = 1h. The popover offers more presets via mouse.
          const target = Date.now() + 60 * 60 * 1000;
          snooze(id, target);
        },
      },
      {
        key: "r",
        run: (id: string) => openRow(id),
      },
      {
        key: "x",
        run: (id: string) => toggleSelect(id),
      },
    ],
    [liveById, archive, markRead, snooze, openRow, toggleSelect],
  );

  const { focusedId } = useListKeyboardNav({
    visibleIds,
    onOpen: openRow,
    onToggleSelect: toggleSelect,
    onSelectAll: selectAllVisible,
    onDeselectAll: deselectAll,
    onFocusSearch: focusSearch,
    disabled: focusId !== null,
    actions: keyboardActions,
  });

  // ── Infinite-scroll sentinel (unchanged) ─────────────────────────────
  const sentinelRef = useRef<HTMLLIElement>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasNextPage) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) fetchNextPage();
      },
      { rootMargin: "200px" },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasNextPage, fetchNextPage]);

  const visibleCount = threadGroups.length;
  // Counts reflect only what's loaded so far (infinite scroll). A trailing "+"
  // signals there are more beyond the loaded page, so the figures don't read as
  // the true mailbox totals — until the BE ships real counts (see below).
  // Counts reflect only what's loaded so far (infinite scroll). A trailing "+"
  // signals there are more beyond the loaded page, so the figures don't read as
  // the true mailbox totals — until the BE ships real counts (see below).
  const morePlus = hasNextPage ? "+" : "";
  const unreadLabel = `${totalUnread}${morePlus}`;
  const totalLabel = `${liveItems.length}${morePlus}`;
  const meta = isLoading ? (
    <span
      aria-hidden
      className="inline-block h-2.5 w-28 animate-pulse rounded bg-black/5 align-middle"
    />
  ) : (
    <Trans>
      <strong className="tabular-nums">{unreadLabel}</strong> non lette ·{" "}
      <span className="tabular-nums">{totalLabel}</span> totali
    </Trans>
  );

  // ── Briefing slot (unchanged shape) ───────────────────────────────────
  // ``setBriefingHasContent`` still feeds ``BriefingSlot onLoaded``; the value
  // itself is no longer read (its only consumer was the CompanionHero, removed
  // with the Jul 2026 command-bar de-layering).
  const [, setBriefingHasContent] = useState(false);
  const briefingSlot = (
    <BriefingSlot domain="inbox" className="mb-5" onLoaded={setBriefingHasContent} />
  );

  // ── Inline-viewer mode ────────────────────────────────────────────────
  if (focusId && resolvedItem) {
    return (
      <FocusedInboxView
        item={resolvedItem}
        focusId={focusId}
        messageId={messageId}
        onBack={() =>
          navigate({
            to: "/workspace/conversations",
            search: clearFocusKeepMsg(messageId),
          })
        }
      />
    );
  }
  if (focusId && !resolvedItem && isLoading) {
    return <FocusedInboxLoading focusId={focusId} messageId={messageId} />;
  }
  if (focusId && !resolvedItem && !isLoading) {
    return (
      <FocusedInboxMissing
        focusId={focusId}
        messageId={messageId}
        onBack={() =>
          navigate({
            to: "/workspace/conversations",
            search: clearFocusKeepMsg(messageId),
          })
        }
      />
    );
  }

  return (
    <>
      <SurfaceLayout
        header={
          // Jul 2026 — single-row command bar, canonical zones. The channel
          // switcher (Tutti · Email · PEC · Chat) is PRIMARY navigation → it
          // lives in the always-visible ``views`` zone; smart-views + providers
          // + period stay behind the shared ``Filtri ▾`` popover.
          <SurfaceCommandBar
            views={<SubTabBar tabId="conversations" chips={chips} className="!px-0 !py-0" />}
            count={meta}
            search={
              <WorkspaceSearchBar
                placeholder={i18n._(msg`Cerca email, chat, PEC…`)}
                ariaLabel={searchAriaLabel}
                initialQuery={search.q}
                onSearch={searchNav.setQuery}
              />
            }
            filters={
              <>
                {/* View mode leads the right cluster (tabs → filters → scope →
                    new): keeps the two ghost buttons (Filtri + account) adjacent
                    instead of splitting them with the segmented toggle. */}
                <InboxGroupingToggle value={groupingMode} onChange={setGroupingMode} />
                <ConversationsFiltersPopover
                  smartView={smartView}
                  onSmartView={setSmartView}
                  smartChips={smartChipDefs}
                  providers={search.providers ?? []}
                  period={search.period ?? "all"}
                  onFilterChange={searchNav.patchFilters}
                  direction={search.direction}
                  onDirectionChange={(d) =>
                    searchNav.patchFilters({ direction: d === "sent" ? "sent" : undefined })
                  }
                />
              </>
            }
            actions={
              <>
                {connectionOptions.length > 0 ? (
                  <ConnectionPicker
                    connections={connectionOptions}
                    activeIds={activeIds}
                    onChange={setActiveIds}
                    label={
                      connectionOptions.length === 1
                        ? i18n._(msg`1 account`)
                        : i18n._(msg`${activeIds.length}/${connectionOptions.length} account`)
                    }
                    ariaLabel={i18n._(msg`Seleziona account conversazioni`)}
                  />
                ) : null}
                <QuickAddMenu
                  area="conversations"
                  label={i18n._(msg`Nuovo`)}
                  ariaLabel={i18n._(msg`Nuova conversazione o azione`)}
                  onConfigureConnectors={() => navigate({ to: "/integrations" })}
                />
              </>
            }
          />
        }
      >
        {/* Wave 3A: first-run memory consent banner — auto-hides after the
            user clicks either CTA (BE writes ``memory_consent_banner_shown_at``). */}
        <MemoryConsentBanner />
        {isError ? (
          <SurfaceEmptyState
            icon={<Inbox size={18} strokeWidth={1.75} />}
            title={i18n._(msg`Impossibile caricare l'inbox`)}
            description={i18n._(
              msg`Riprova fra qualche secondo. Se il problema persiste, verifica la connessione ai provider email/chat.`,
            )}
          />
        ) : isLoading ? (
          // Fresh fetch with no cached data yet — the FIRST page load AND every
          // new search query (the live provider fanout is slow: Gmail q= /
          // Outlook $search / PEC round-trips, gated by the slowest account).
          // Show a full skeleton list so the wait reads as "working", not a
          // blank pane. Search-aware label so a query says "Cerco…".
          <>
            {briefingSlot}
            <SurfaceList flat className="-mx-8">
              <InboxLoadingRows
                count={7}
                label={
                  (search.q ?? "").trim()
                    ? i18n._(msg`Cerco in tutti gli account…`)
                    : i18n._(msg`Carico le conversazioni…`)
                }
              />
            </SurfaceList>
          </>
        ) : visibleCount === 0 ? (
          <>
            {briefingSlot}
            <EmptyStateForContext
              smartView={smartView}
              subTab={activeSubTab}
              totalUnread={totalUnread}
              query={search.q}
              onClearQuery={() => searchNav.setQuery("")}
              onClearView={() => setSmartView("tutti")}
            />
          </>
        ) : (
          <>
            {briefingSlot}
            <div aria-live="polite" aria-atomic="true" className="sr-only">
              {isLoading
                ? i18n._(msg`Caricamento conversazioni in corso`)
                : selectedIds.size > 0
                  ? i18n._(msg`${visibleCount} thread visibili, ${selectedIds.size} selezionati`)
                  : i18n._(msg`${visibleCount} thread visibili`)}
            </div>
            <SurfaceList flat className="-mx-8">
              {threadGroups.map((group, idx) => {
                const item = liveToInboxItem(group.newest);
                const live = group.newest;
                return (
                  <ThreadRow
                    key={group.thread_id}
                    item={item}
                    live={live}
                    index={idx}
                    threadCount={group.count}
                    selected={selectedIds.has(item.message_id)}
                    selectionActive={selectedIds.size > 0}
                    focused={focusedId === item.message_id}
                    isActive={focusId === item.message_id}
                    onOpen={() => openRow(item.message_id)}
                    onToggleSelect={() => toggleSelect(item.message_id)}
                    onToggleRead={() => void markRead(item.message_id, item.is_unread)}
                    onArchive={() => void archive(live)}
                    onSnooze={(epochMs) => snooze(item.message_id, epochMs)}
                    onToggleStar={() => toggleStar(item.message_id)}
                    summaryProvider={resolvedSummaryProvider}
                  />
                );
              })}
              {isFetchingNextPage ? <InboxLoadingRows count={3} /> : null}
              {hasNextPage ? (
                <li ref={sentinelRef} aria-hidden className="h-1 list-none" />
              ) : visibleCount > 0 ? (
                <li className="px-8 py-4 text-center text-[11px] text-ink-3">
                  <Trans>Fine elenco · {visibleCount} thread</Trans>
                </li>
              ) : null}
            </SurfaceList>
          </>
        )}
      </SurfaceLayout>

      <InboxBulkActionBar
        count={selectedIds.size}
        onDeselectAll={deselectAll}
        onMarkRead={() => void markReadMany(Array.from(selectedIds), true).then(deselectAll)}
        onArchive={() => void archiveMany(selectedItemsLive).then(deselectAll)}
        onSnooze={(epochMs) => {
          snoozeMany(Array.from(selectedIds), epochMs);
          deselectAll();
        }}
        onToggleStar={() => {
          for (const id of selectedIds) toggleStar(id);
          deselectAll();
        }}
      />
    </>
  );
}

// ── Row wrapper — keeps the star-overlay hook scoped per row ──────────

interface ThreadRowProps {
  readonly item: InboxItem;
  readonly live: InboxLiveItem;
  readonly index: number;
  readonly threadCount: number;
  readonly selected: boolean;
  readonly selectionActive: boolean;
  readonly focused: boolean;
  readonly isActive: boolean;
  readonly onOpen: () => void;
  readonly onToggleSelect: () => void;
  readonly onToggleRead: () => void;
  readonly onArchive: () => void;
  readonly onSnooze: (epochMs: number) => void;
  readonly onToggleStar: () => void;
  readonly summaryProvider: (mid: string) => string | null;
}

const ThreadRow: React.FC<ThreadRowProps> = ({
  item,
  live,
  index,
  threadCount,
  selected,
  selectionActive,
  focused,
  isActive,
  onOpen,
  onToggleSelect,
  onToggleRead,
  onArchive,
  onSnooze,
  onToggleStar,
  summaryProvider,
}) => {
  const isStarred = useIsStarred(item.message_id, live.is_starred);
  const queryClient = useQueryClient();
  // Prefetch the email thread on hover/focus so opening the row is instant
  // (the drawer's ``useEmailThread`` reads the same 60s-cached slice). Only
  // email/PEC rows open a thread — chat rows use a different detail path.
  const prefetchThread = useCallback(() => {
    if (item.channel !== "email" && item.channel !== "pec") return;
    void queryClient.prefetchQuery(
      emailThreadQueryOptions(item.message_id, {
        provider: item.live_provider,
        account: item.live_account,
      }),
    );
  }, [queryClient, item.channel, item.message_id, item.live_provider, item.live_account]);
  // Only Gmail / Outlook expose archive. PEC + chat rows omit the
  // ``onArchive`` prop so the action button disappears (DRY: the row
  // already hides the action when the handler is undefined).
  const supportsArchive = live.provider === "gmail" || live.provider === "outlook";
  return (
    <InboxItemRow
      item={item}
      index={index}
      onHoverPrefetch={prefetchThread}
      threadCount={threadCount}
      isStarred={isStarred}
      isIngested={live.is_ingested}
      selected={selected}
      selectionActive={selectionActive}
      isActive={isActive}
      data-focused={focused ? "true" : undefined}
      onClick={onOpen}
      onToggleSelect={onToggleSelect}
      onToggleRead={onToggleRead}
      onArchive={supportsArchive ? onArchive : undefined}
      onSnooze={(_it, epochMs) => onSnooze(epochMs)}
      onToggleStar={onToggleStar}
      summaryProvider={summaryProvider}
    />
  );
};
ThreadRow.displayName = "ThreadRow";

// ── Empty state — per-tab + per-view variants ─────────────────────────

const EmptyStateForContext: React.FC<{
  readonly smartView: InboxSmartView;
  readonly subTab: string;
  readonly totalUnread: number;
  readonly query: string | undefined;
  readonly onClearQuery: () => void;
  readonly onClearView: () => void;
}> = ({ smartView, subTab, totalUnread, query, onClearQuery, onClearView }) => {
  const { i18n } = useLingui();
  if (query && query.trim().length > 0) {
    return (
      <SurfaceEmptyState
        icon={<Inbox size={18} strokeWidth={1.75} />}
        title={i18n._(msg`Nessun risultato per la ricerca`)}
        description={i18n._(msg`"${query}" non corrisponde a nessun messaggio visibile.`)}
        action={
          <Button variant="secondary" size="md" onClick={onClearQuery}>
            <Trans>Pulisci ricerca</Trans>
          </Button>
        }
      />
    );
  }
  if (smartView !== "tutti") {
    return (
      <SurfaceEmptyState
        icon={<Inbox size={18} strokeWidth={1.75} />}
        title={i18n._(msg`Nessun messaggio in questa vista`)}
        description={i18n._(msg`Cambia vista per vedere altri messaggi.`)}
        action={
          <Button variant="secondary" size="md" onClick={onClearView}>
            <Trans>Mostra tutti</Trans>
          </Button>
        }
      />
    );
  }
  if (subTab === "pec") {
    return (
      <SurfaceEmptyState
        icon={<Shield size={18} strokeWidth={1.75} />}
        title={i18n._(msg`Nessuna PEC oggi`)}
        description={i18n._(msg`Le ricevute SDI e i messaggi certificati arriveranno qui.`)}
      />
    );
  }
  if (subTab === "slack" || subTab === "teams" || subTab === "google_chat") {
    return (
      <SurfaceEmptyState
        icon={<MessageSquare size={18} strokeWidth={1.75} />}
        title={i18n._(msg`Tutto tranquillo`)}
        description={i18n._(msg`Nessuna conversazione attiva in questo canale.`)}
      />
    );
  }
  if (totalUnread === 0) {
    return (
      <SurfaceEmptyState
        icon={<Inbox size={18} strokeWidth={1.75} />}
        title={i18n._(msg`Inbox zero!`)}
        description={i18n._(msg`Nessun messaggio non letto. Goditi il momento.`)}
      />
    );
  }
  return (
    <SurfaceEmptyState
      icon={<Inbox size={18} strokeWidth={1.75} />}
      title={i18n._(msg`Nessun messaggio`)}
      description={i18n._(msg`Quando arriveranno nuove email o notifiche li vedrai qui.`)}
    />
  );
};
EmptyStateForContext.displayName = "EmptyStateForContext";

// ── Inline-viewer sub-components (unchanged) ──────────────────────────

const INBOX_BACK_LABEL_MSG = msg`Torna all'inbox`;

const CHANNEL_EYEBROW_MSG: Record<InboxChannel, ReturnType<typeof msg>> = {
  email: msg`Email`,
  slack: msg`Slack`,
  teams: msg`Teams`,
  google_chat: msg`Google Chat`,
  pec: msg`PEC · SDI`,
};

const CHANNEL_ICON_FOR_HEADER: Record<
  InboxChannel,
  React.ComponentType<{ className?: string; "aria-hidden"?: boolean }>
> = {
  email: Mail,
  slack: MessageSquare,
  teams: MessageSquare,
  google_chat: MessageSquare,
  pec: Shield,
};

const FocusedInboxView: React.FC<{
  item: InboxItem;
  focusId: string;
  messageId: string | null;
  onBack: () => void;
}> = ({ item, focusId, messageId, onBack }) => {
  const { i18n } = useLingui();
  const ChannelIcon = CHANNEL_ICON_FOR_HEADER[item.channel];
  const eyebrowSuffix = `${i18n._(CHANNEL_EYEBROW_MSG[item.channel])} · ${formatRelativeTime(item.received_at)}`;
  return (
    <FocusedViewLayout
      tabLabel={i18n._(msg`Conversazioni`)}
      eyebrowSuffix={eyebrowSuffix}
      title={item.subject || item.sender_display || i18n._(msg`(senza oggetto)`)}
      subtitle={
        item.sender_display ? (
          <span className="truncate font-mono">{item.sender_display}</span>
        ) : undefined
      }
      icon={<ChannelIcon className="h-3.5 w-3.5" aria-hidden />}
      backAriaLabel={i18n._(INBOX_BACK_LABEL_MSG)}
      onBack={onBack}
      messageId={messageId}
      focusId={focusId}
    >
      <InboxDetailPane item={item} onClose={onBack} />
    </FocusedViewLayout>
  );
};

const FocusedInboxLoading: React.FC<{
  focusId: string;
  messageId: string | null;
}> = ({ focusId, messageId }) => {
  const { i18n } = useLingui();
  return (
    <FocusedViewLayout
      tabLabel={i18n._(msg`Conversazioni`)}
      title={i18n._(msg`Caricamento messaggio…`)}
      icon={<Mail className="h-3.5 w-3.5" aria-hidden />}
      backAriaLabel={i18n._(INBOX_BACK_LABEL_MSG)}
      messageId={messageId}
      focusId={focusId}
    >
      <div className="grid h-full place-items-center px-6 py-12 text-center">
        <p className="text-[12px] text-ink-3" aria-busy>
          <Trans>Carico il messaggio…</Trans>
        </p>
      </div>
    </FocusedViewLayout>
  );
};

const FocusedInboxMissing: React.FC<{
  focusId: string;
  messageId: string | null;
  onBack: () => void;
}> = ({ focusId, messageId, onBack }) => {
  const { i18n } = useLingui();
  return (
    <FocusedViewLayout
      tabLabel={i18n._(msg`Conversazioni`)}
      title={i18n._(msg`Messaggio non trovato`)}
      icon={<Inbox className="h-3.5 w-3.5" aria-hidden />}
      backAriaLabel={i18n._(INBOX_BACK_LABEL_MSG)}
      onBack={onBack}
      messageId={messageId}
      focusId={focusId}
    >
      <SurfaceEmptyState
        icon={<Inbox size={18} strokeWidth={1.75} />}
        title={i18n._(msg`Questo messaggio non è più disponibile`)}
        description={i18n._(
          msg`Potrebbe essere stato archiviato o cancellato. Torna all'inbox per cercarlo.`,
        )}
        action={
          <Button variant="primary" size="md" onClick={onBack}>
            <ArrowLeft aria-hidden />
            <Trans>Torna all'inbox</Trans>
          </Button>
        }
      />
    </FocusedViewLayout>
  );
};

const SKELETON_ROW_KEYS = [
  "sk-a",
  "sk-b",
  "sk-c",
  "sk-d",
  "sk-e",
  "sk-f",
  "sk-g",
  "sk-h",
] as const;

function InboxLoadingRows({
  count,
  label,
}: {
  readonly count: number;
  /** Footer text. Defaults to the pagination wording ("Carico altri messaggi…");
   *  the initial / search skeleton passes a context-aware label instead. */
  readonly label?: React.ReactNode;
}) {
  const rows = SKELETON_ROW_KEYS.slice(0, Math.min(count, SKELETON_ROW_KEYS.length));
  return (
    <>
      {rows.map((key) => (
        <li
          key={key}
          aria-hidden
          className="flex items-center gap-3 border-black/5 border-b py-3.5 pr-8 pl-5 last:border-b-0"
        >
          <div className="h-7 w-7 shrink-0 animate-pulse rounded-md bg-black/5" />
          <div className="min-w-0 flex-1 space-y-1.5">
            <div className="h-2 w-1/3 animate-pulse rounded bg-black/5" />
            <div className="h-2.5 w-2/3 animate-pulse rounded bg-black/5" />
            <div className="h-2 w-1/2 animate-pulse rounded bg-black/5" />
          </div>
        </li>
      ))}
      <li
        aria-live="polite"
        className="flex items-center justify-center gap-1.5 px-8 py-2 text-[11px] text-ink-3"
      >
        <Loader2 size={12} strokeWidth={2} aria-hidden className="animate-spin" />
        {label ?? <Trans>Carico altri messaggi…</Trans>}
      </li>
    </>
  );
}
